import React, { useCallback, useEffect } from "react";
import { ResponsiveCard } from "../components/ResponsiveCard";
import {
  Button,
  List,
  Select,
  Skeleton,
  Space,
  Tag,
  message,
} from "antd";
import { ReloadOutlined, RightCircleFilled, SyncOutlined } from "@ant-design/icons";
import { Platform } from "../../types/platform";
import { WizardService } from "../../services/WizardService";
import { Sdk, SdkStability } from "../../types/sdk";
import { AppRoutes } from "../../AppRoutes";
import Search from "antd/es/input/Search";
import { SearchProps } from "antd/lib/input";
import { PlatformIcon } from "../components/PlatformIcon";
import { describeCrawlerError } from "./crawlerErrors";

export interface MediationCrawlerCardProps {
  title: React.ReactNode;
  allowVersioning?: boolean;
  mediation: string;
  platform: Platform;
  repos?: string[];
  defaultRepo?: string;
  children?: React.ReactNode;
}

const formattedDate = (date?: string) => {
  if (date) {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
};

const stability = (stability?: SdkStability) => {
  switch (stability) {
    case SdkStability.STABLE:
      return (
        <Tag style={{ marginInlineEnd: 0 }} color="green">
          STABLE
        </Tag>
      );
    case SdkStability.BETA:
      return (
        <Tag style={{ marginInlineEnd: 0 }} color="orange">
          BETA
        </Tag>
      );
    case SdkStability.ALPHA:
      return (
        <Tag style={{ marginInlineEnd: 0 }} color="red">
          ALPHA
        </Tag>
      );
    case SdkStability.CUSTOM:
      return (
        <Tag style={{ marginInlineEnd: 0 }} color="gray">
          CUSTOM
        </Tag>
      );
  }
};

export const MediationCrawlerCard = (props: MediationCrawlerCardProps) => {
  const api = WizardService.Sdk;
  const crawler = WizardService.Crawler;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isCrawling, setIsCrawling] = React.useState(false);
  const [isReindexing, setIsReindexing] = React.useState(false);
  const [sdks, setSdks] = React.useState<Sdk[]>([]);
  const [repository, setRepository] = React.useState<string | undefined>(props.defaultRepo);

  const fetch = useCallback(() => {
    setIsLoading(true);
    api
      .latestVersions(props.mediation, props.platform, 5)
      .then((sdks) => setSdks(sdks))
      .finally(() => setIsLoading(false));
  }, [api, props.mediation, props.platform]);

  const onSearch: SearchProps["onSearch"] = (value, _e) => {
    crawl(value);
  };

  const crawl = (version?: string) => {
    setIsCrawling(true);
    crawler
      .synchronize(props.mediation, props.platform, repository, version)
      .catch((error) => message.error(describeCrawlerError(error)))
      .finally(() => {
        setIsCrawling(false);
        fetch();
      });
  };

  const reindex = () => {
    setIsReindexing(true);
    crawler
      .reindex(props.mediation, props.platform)
      .then(() => message.success("Reindex started"))
      .catch((error) => message.error(describeCrawlerError(error)))
      .finally(() => {
        setIsReindexing(false);
        fetch();
      });
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  const Content = () => {
    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <List
          size="small"
          dataSource={sdks}
          renderItem={(sdk) => (
            <List.Item
              actions={[
                <Button
                  type="link"
                  size="small"
                  href={"/" + AppRoutes.Wizard.Sdk.replace(":id", sdk.id!.toString())}
                  icon={<RightCircleFilled />}
                />,
              ]}
            >
              <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                <span style={{ fontWeight: 500, width: 80, flexShrink: 0 }}>{sdk.version}</span>
                <span style={{ width: 70, flexShrink: 0 }}>{stability(sdk.stability)}</span>
                <span style={{ color: "#888", marginLeft: "auto" }}>{formattedDate(sdk.updatedAt)}</span>
              </div>
            </List.Item>
          )}
        />
        <Space direction="vertical">
          {props.allowVersioning ? (
            <Search
              enterButton={<SyncOutlined />}
              addonBefore={
                props.repos && (
                  <Select
                    allowClear
                    placeholder="Select a repository"
                    style={{ minWidth: 200, flex: 1 }}
                    options={props.repos.map((repo) => ({ value: repo }))}
                    value={repository || undefined}
                    onChange={(repo) => setRepository(repo)}
                  />
                )
              }
              placeholder="Latest version"
              loading={isCrawling}
              onSearch={onSearch}
            />
          ) : (
            <Space.Compact>
              {props.repos && (
                <Select
                  allowClear
                  placeholder="Select a repository"
                  style={{ minWidth: 200, flex: 1 }}
                  options={props.repos.map((repo) => ({ value: repo }))}
                  value={repository || undefined}
                  onChange={(repo) => setRepository(repo)}
                />
              )}
              <Button
                loading={isCrawling}
                type="primary"
                onClick={() => crawl()}
                icon={<SyncOutlined />}
              >
                Crawl Latest
              </Button>
            </Space.Compact>
          )}
          {props.children}
        </Space>
      </Space>
    );
  };

  return (
    <ResponsiveCard
      contentPadding="1rem"
      title={
        <Space>
          <PlatformIcon platform={props.platform} />
          {props.title}
        </Space>
      }
      extra={
        <Button
          danger
          size="small"
          loading={isReindexing}
          onClick={reindex}
          icon={<ReloadOutlined />}
        >
          Reindex
        </Button>
      }
    >
      {isLoading ? <Skeleton /> : <Content />}
    </ResponsiveCard>
  );
};
